import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type Dispatch,
  type ReactNode
} from "react";
import {
  ProvetBookingFlowAction,
  ProvetBookingFlowState,
  getInitialProvetBookingFlowState,
  provetBookingFlowReducer
} from "../hooks/provetBookingFlowReducer";

/**
 * Booking flow context for Provet booking.
 * Contains the booking flow state that changes frequently.
 * Isolated from static config and data to minimize re-renders.
 */
interface ProvetBookingFlowContextType {
  bookingFlowState: ProvetBookingFlowState;
  bookingFlowDispatch: Dispatch<ProvetBookingFlowAction>;
}

interface ProvetBookingFlowProviderProps {
  children: ReactNode;
  sessionId: string;
}

const ProvetBookingFlowContext = createContext<ProvetBookingFlowContextType | undefined>(
  undefined
);

export const ProvetBookingFlowProvider = ({
  children,
  sessionId
}: ProvetBookingFlowProviderProps) => {
  const [bookingFlowState, bookingFlowDispatch] = useReducer(
    provetBookingFlowReducer,
    getInitialProvetBookingFlowState(sessionId)
  );

  // Memoize context value to prevent unnecessary re-renders
  // Note: bookingFlowState changes frequently, but we want to ensure
  // the context object itself is stable
  const value = useMemo(
    () => ({
      bookingFlowState,
      bookingFlowDispatch
    }),
    [bookingFlowState, bookingFlowDispatch]
  );

  return (
    <ProvetBookingFlowContext.Provider value={value}>
      {children}
    </ProvetBookingFlowContext.Provider>
  );
};

export const useProvetBookingFlow = () => {
  const context = useContext(ProvetBookingFlowContext);
  if (context === undefined) {
    throw new Error(
      "useProvetBookingFlow must be used within a ProvetBookingFlowProvider"
    );
  }
  return context;
};

export type { ProvetBookingFlowContextType };
